import {render} from 'vike/abort';
import {getPrimaryDomain} from '../../tenant.config.js';
import {isSsrRateLimited, isCrawlerRequest} from './ssr-rate-limit.js';
import {withSite} from './api.js';

// Shared: data() hooks run this server-side on first load and client-side on
// every navigation after (+data.shared.js), so nothing here may import a
// Node-only module — crypto.subtle (Web Crypto) works in both.
//
// SSR requests are signed (SSR_API_SECRET) so the API's rate limiter trusts
// this container instead of banning its one shared IP for bot-like traffic.
// The secret is read only under import.meta.env.SSR, a build-time constant
// Vite dead-code-eliminates for the client build, so it never reaches the
// browser; client-side calls are simply left unsigned.
const SSR_API_SECRET = import.meta.env.SSR ? (process.env.SSR_API_SECRET || '') : '';
if (import.meta.env.SSR && !SSR_API_SECRET) {
    console.warn('[server-fetch] SSR_API_SECRET is not set; API requests are unsigned and subject to per-IP rate limiting');
}

async function hmacSha256Hex(secret, message) {
    const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(secret), {name: 'HMAC', hash: 'SHA-256'}, false, ['sign']);
    const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(message));
    return [...new Uint8Array(signature)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function signatureHeaders(url, method = 'GET') {
    if (!SSR_API_SECRET) return {};
    const {pathname, search} = new URL(url);
    const timestamp = Math.floor(Date.now() / 1000).toString();
    // Must match RateLimiter::hasValidServerSignature() in the API: the API
    // verifies against REQUEST_URI, which is exactly pathname + search as sent.
    const signature = await hmacSha256Hex(SSR_API_SECRET, `${timestamp}\n${method}\n${pathname}${search}`);
    return {'X-SSR-Timestamp': timestamp, 'X-SSR-Signature': signature};
}

// The API rejects requests with no Referer; browsers send one automatically,
// but Node's fetch() doesn't, so SSR calls set it explicitly. No-op
// client-side — Referer is a forbidden fetch() header anyway.
export function refererHeaders(pageContext) {
    if (!import.meta.env.SSR) return {};
    // Primary domain, not pageContext.hostname, so alias domains still pass this check.
    const hostname = getPrimaryDomain(pageContext.site);
    const pathname = pageContext.urlParsed?.pathname ?? pageContext.urlPathname ?? '/';
    return {
        headers: {
            Referer: `https://${hostname}${pathname}`,
        },
    };
}

// Shared by every +data.js: fetch, parse, and check the API's {code: 200}
// envelope. `code` is the API's own envelope code (e.g. 404 for "subscriber
// not found") or null when the API couldn't be reached or didn't return JSON,
// so callers that must act on a definitive API answer (vanity/+data.shared.js
// redirecting disabled subscribers) can leave transient failures to the
// client-side refetch instead. `pageContext`, when passed, rate-limits the
// signed channel (see ssr-rate-limit.js) — pass it inside a +data.shared.js hook.
export async function fetchApiJson(url, options = {}, pageContext = null) {
    if (import.meta.env.SSR && pageContext && isSsrRateLimited(pageContext)) {
        // A crawler gets a real 429 instead of a 200 shell it'll never
        // hydrate; a real visitor gets the null-seed fallback below and
        // retries client-side.
        if (isCrawlerRequest(pageContext)) {
            throw render(429);
        }
        return {ok: false, data: null, code: null};
    }
    if (pageContext?.site) {
        url = withSite(url, pageContext.site);
    }
    try {
        const response = await fetch(url, {
            ...options,
            headers: {...(options.headers ?? {}), ...(await signatureHeaders(url, options.method ?? 'GET'))},
        });
        const json = await response.json();
        const code = typeof json.code === 'number' ? json.code : null;
        return code === 200 ? {ok: true, data: json.data, code} : {ok: false, data: null, code};
    } catch {
        return {ok: false, data: null, code: null};
    }
}
